import React, { useState, useEffect } from 'react';

import { useCurrentUserId } from '$shared/custom-hooks/useCurrentUserId';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';

import { useUpdateUser, useGetUser } from '$features/users/users.api';

import { ChatDialog } from './chat-dialog';
import { UserConsent } from './user-consent';

interface ChatBotWrapperProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChatBotWrapper({ isOpen, onClose }: ChatBotWrapperProps) {
  const [hasConsented, setHasConsented] = useState<boolean | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const [showChat, setShowChat] = useState(false);

  const { email: emailFromToken, tenantId } = useTokenAttributes(['email', 'tenantId']);
  const currentUserId = useCurrentUserId(emailFromToken, tenantId) || '';
  const { data: userData } = useGetUser(currentUserId || '');
  const { mutateAsync: mutateUpdateUser, isPending: isLoading } = useUpdateUser();

  // Load user consent status when component mounts or when isOpen changes
  useEffect(() => {
    if (isOpen && hasConsented === null) {
      const consented = userData?.acceptedChatBotConsent ?? false;
      setHasConsented(consented);
      if (consented) {
        setShowChat(true);
      } else {
        setShowConsent(true);
      }
    } else if (isOpen && hasConsented === true) {
      // If dialog is opened and user has already consented, show chat directly
      setShowChat(true);
    } else if (isOpen && hasConsented === false) {
      // If dialog is opened and user has declined consent, show consent dialog
      setShowConsent(true);
    }
  }, [isOpen, hasConsented]);

  // Reset state when dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setShowConsent(false);
      setShowChat(false);
    }
  }, [isOpen]);

  const handleConsentAccept = async () => {
    await mutateUpdateUser({
      id: currentUserId,
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      acceptedChatBotConsent: true,
    });
    setHasConsented(true);
    setShowConsent(false);
    setShowChat(true);
  };

  const handleConsentDecline = async () => {
    await mutateUpdateUser({
      id: currentUserId,
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      acceptedChatBotConsent: false,
    });
    setHasConsented(false);
    setShowConsent(false);
    onClose();
  };

  const handleChatClose = () => {
    setShowChat(false);
    setShowConsent(false);
    onClose();
  };

  return (
    <>
      {showConsent && (
        <UserConsent
          isOpen={showConsent}
          onAccept={handleConsentAccept}
          onDecline={handleConsentDecline}
          isLoading={isLoading}
        />
      )}
      {!showConsent && <ChatDialog isOpen={showChat} onClose={handleChatClose} />}
    </>
  );
}
